import { dbExecute, dbSelect } from "./db";

// ==========================================
// REPOSITÓRIO: CONFIGURAÇÕES E LICENCIAMENTO
// ==========================================

export const getLocalSetting = async (key: string): Promise<string | null> => {
  const result = await dbSelect<{ value: string }>(
    "SELECT value FROM system_settings WHERE key = ?",
    [key]
  );
  return result.length > 0 ? result[0].value : null;
};

export const setLocalSetting = async (key: string, value: string): Promise<void> => {
  await dbExecute(
    "INSERT OR REPLACE INTO system_settings (key, value) VALUES (?, ?)",
    [key, value]
  );
};

// ==========================================
// REPOSITÓRIO: CONDOMÍNIO
// ==========================================

export const saveCondominium = async (condo: {
  id: string;
  name: string;
  address?: string | null;
  created_at?: string | null;
}): Promise<void> => {
  await dbExecute(
    "INSERT OR REPLACE INTO condominiums (id, name, address, created_at) VALUES (?, ?, ?, ?)",
    [condo.id, condo.name, condo.address || null, condo.created_at || new Date().toISOString()]
  );
};

export const getLocalCondominium = async (): Promise<any | null> => {
  const result = await dbSelect<any>("SELECT * FROM condominiums LIMIT 1");
  return result.length > 0 ? result[0] : null;
};

// ==========================================
// REPOSITÓRIO: UNIDADES (Sincronização Híbrida)
// ==========================================

export const saveUnits = async (units: any[]): Promise<void> => {
  for (const u of units) {
    await dbExecute(
      `INSERT OR REPLACE INTO units (id, condominium_id, number, building, block, created_at) 
       VALUES (?, ?, ?, ?, ?, ?)`,
      [u.id, u.condominium_id, u.number, u.building || null, u.block || null, u.created_at || new Date().toISOString()]
    );
  }
};

export const getLocalUnits = async (): Promise<any[]> => {
  return await dbSelect<any>("SELECT * FROM units ORDER BY building, number");
};

// ==========================================
// REPOSITÓRIO: PERFIS (Moradores e Colaboradores)
// ==========================================

export const saveProfiles = async (profiles: any[]): Promise<void> => {
  for (const p of profiles) {
    await dbExecute(
      `INSERT OR REPLACE INTO profiles (
        id, condominium_id, email, full_name, phone, cpf, role, 
        apartment_number, building, is_active, password_hash, created_at
      ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
      [
        p.id,
        p.condominium_id,
        p.email || null,
        p.full_name || null,
        p.phone || null,
        p.cpf || null,
        p.role || "morador",
        p.apartment_number || null,
        p.building || null,
        p.is_active !== undefined ? (p.is_active ? 1 : 0) : 1,
        p.password_hash || null,
        p.created_at || new Date().toISOString()
      ]
    );
  }
};

export const getLocalProfiles = async (role?: string): Promise<any[]> => {
  if (role) {
    return await dbSelect<any>("SELECT * FROM profiles WHERE role = ? AND is_active = 1", [role]);
  }
  return await dbSelect<any>("SELECT * FROM profiles WHERE is_active = 1");
};

export const getLocalProfileByEmailOrCpf = async (identifier: string): Promise<any | null> => {
  const cleanId = identifier.trim();
  const result = await dbSelect<any>(
    "SELECT * FROM profiles WHERE (email = ? OR cpf = ?) AND is_active = 1 LIMIT 1",
    [cleanId, cleanId]
  );
  return result.length > 0 ? result[0] : null;
};

// ==========================================
// REPOSITÓRIO: VISITANTES E HISTÓRICO DE ACESSOS
// ==========================================

const mapVisitorWithDetails = (rows: any[]): any[] => {
  return rows.map((r) => {
    // Extrai o registro do visitante mapeando chaves estrangeiras join
    const visitorObj = { ...r };
    
    // Remove os prefixos de join do objeto raiz para evitar ruído
    const keysToRemove = [
      "u_id", "u_number", "u_building", "u_block",
      "p_id", "p_full_name", "p_email", "p_role"
    ];
    keysToRemove.forEach(k => delete visitorObj[k]);

    return {
      ...visitorObj,
      // Garante boleano nos status
      is_active: r.is_active === 1,
      unit: r.u_id ? {
        id: r.u_id,
        number: r.u_number,
        building: r.u_building,
        block: r.u_block
      } : null,
      authorized_by_user: r.p_id ? {
        id: r.p_id,
        full_name: r.p_full_name,
        email: r.p_email,
        role: r.p_role
      } : null
    };
  });
};

export const getLocalVisitors = async (): Promise<any[]> => {
  const rows = await dbSelect<any>(`
    SELECT 
      v.*,
      u.id as u_id, u.number as u_number, u.building as u_building, u.block as u_block,
      p.id as p_id, p.full_name as p_full_name, p.email as p_email, p.role as p_role
    FROM visitors v
    LEFT JOIN units u ON v.unit_id = u.id
    LEFT JOIN profiles p ON v.authorized_by = p.id
    ORDER BY v.created_at DESC
  `);
  return mapVisitorWithDetails(rows);
};

export const getLocalVisitorsByDateRange = async (
  startDate: string,
  endDate: string
): Promise<any[]> => {
  const rows = await dbSelect<any>(
    `SELECT 
      v.*,
      u.id as u_id, u.number as u_number, u.building as u_building, u.block as u_block,
      p.id as p_id, p.full_name as p_full_name, p.email as p_email, p.role as p_role
    FROM visitors v
    LEFT JOIN units u ON v.unit_id = u.id
    LEFT JOIN profiles p ON v.authorized_by = p.id
    WHERE v.created_at >= ? AND v.created_at <= ?
    ORDER BY v.created_at DESC`,
    [`${startDate}T00:00:00`, `${endDate}T23:59:59`]
  );
  return mapVisitorWithDetails(rows);
};

export const createLocalVisitor = async (v: {
  name: string;
  document?: string | null;
  phone?: string | null;
  photo_url?: string | null;
  notes?: string | null;
  unit_id?: string | null;
  condominium_id: string;
  authorized_by?: string | null;
  vehicle_plate?: string | null;
  visitor_type?: string | null;
}): Promise<any> => {
  const id = crypto.randomUUID();
  const createdAt = new Date().toISOString();
  const entryTime = createdAt;
  const status = "active";

  await dbExecute(
    `INSERT INTO visitors (
      id, condominium_id, name, document, phone, photo_url, notes, 
      status, unit_id, authorized_by, vehicle_plate, visit_date, 
      visitor_type, entry_time, exit_time, created_at
    ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
    [
      id,
      v.condominium_id,
      v.name,
      v.document || null,
      v.phone || null,
      v.photo_url || null,
      v.notes || null,
      status,
      v.unit_id || null,
      v.authorized_by || null,
      v.vehicle_plate || null,
      createdAt.split("T")[0],
      v.visitor_type || "visitor",
      entryTime,
      null, // exit_time é null na entrada
      createdAt
    ]
  );

  // Retorna o registro criado com as tabelas de detalhes
  const result = await dbSelect<any>(
    `SELECT 
      v.*,
      u.id as u_id, u.number as u_number, u.building as u_building, u.block as u_block,
      p.id as p_id, p.full_name as p_full_name, p.email as p_email, p.role as p_role
    FROM visitors v
    LEFT JOIN units u ON v.unit_id = u.id
    LEFT JOIN profiles p ON v.authorized_by = p.id
    WHERE v.id = ? LIMIT 1`,
    [id]
  );
  return mapVisitorWithDetails(result)[0];
};

export const updateLocalVisitorExit = async (id: string): Promise<any> => {
  const exitTime = new Date().toISOString();
  await dbExecute(
    "UPDATE visitors SET exit_time = ?, status = 'completed' WHERE id = ?",
    [exitTime, id]
  );

  const result = await dbSelect<any>(
    `SELECT 
      v.*,
      u.id as u_id, u.number as u_number, u.building as u_building, u.block as u_block,
      p.id as p_id, p.full_name as p_full_name, p.email as p_email, p.role as p_role
    FROM visitors v
    LEFT JOIN units u ON v.unit_id = u.id
    LEFT JOIN profiles p ON v.authorized_by = p.id
    WHERE v.id = ? LIMIT 1`,
    [id]
  );
  return mapVisitorWithDetails(result)[0];
};

export const searchLocalVisitors = async (
  term: string,
  condominiumId: string
): Promise<any[]> => {
  const safeTerm = `%${term.trim()}%`;
  // Busca no SQLite local
  const rows = await dbSelect<any>(
    `SELECT 
      v.id, v.name, v.document, v.photo_url, v.notes, v.created_at,
      u.id as u_id, u.number as u_number, u.building as u_building, u.block as u_block
    FROM visitors v
    LEFT JOIN units u ON v.unit_id = u.id
    WHERE v.condominium_id = ? AND (v.name LIKE ? OR v.document LIKE ?)
    ORDER BY v.created_at DESC
    LIMIT 40`,
    [condominiumId, safeTerm, safeTerm]
  );

  return rows.map((r) => ({
    id: r.id,
    name: r.name,
    document: r.document,
    photo_url: r.photo_url,
    notes: r.notes,
    created_at: r.created_at,
    unit: r.u_id ? {
      id: r.u_id,
      number: r.u_number,
      building: r.u_building
    } : null
  }));
};

export const getLocalVisitorByDocument = async (
  document: string,
  condominiumId: string
): Promise<any | null> => {
  const result = await dbSelect<any>(
    `SELECT 
      v.*,
      u.id as u_id, u.number as u_number, u.building as u_building
    FROM visitors v
    LEFT JOIN units u ON v.unit_id = u.id
    WHERE v.condominium_id = ? AND v.document = ?
    ORDER BY v.created_at DESC
    LIMIT 1`,
    [condominiumId, document]
  );
  if (result.length === 0) return null;
  const r = result[0];
  return {
    ...r,
    unit: r.u_id ? {
      id: r.u_id,
      number: r.u_number,
      building: r.u_building
    } : null
  };
};

